import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { orgRoleHasScope, type AreaScope, type OrgRole } from '@releve/shared';
import { useUserOrganizations } from '@/hooks/useUserOrganizations';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useIsSuperAdmin } from '@/lib/permissions';

export interface CurrentUserOrgRole {
  /** The membership row's role — `null` when the user holds no membership (e.g. a super admin). */
  role: string | null;
  /**
   * Does the caller carry `scope` in this org? (shared `orgRoleHasScope`,
   * docs/USER_ROLES_AND_PERMISSIONS.md). Never `admin` — use `isAdmin`.
   */
  can: (scope: AreaScope) => boolean;
  isAdmin: boolean;
  /** Holds any membership role in the org (admin, member, accountant, project_manager). */
  isMember: boolean;
  isLoading: boolean;
}

/**
 * The role the scope matrix is evaluated against: the membership role, or —
 * for a platform super admin with no membership row — `member`. Mirrors the
 * API's `requireOrgMember`, which resolves a non-member super admin to
 * `member`, so the web never hides an area the API would serve.
 */
export function effectiveScopeRole(
  role: string | null | undefined,
  isSuperAdmin: boolean | undefined,
): string | null {
  if (role) return role;
  return isSuperAdmin === true ? ('member' satisfies OrgRole) : null;
}

/**
 * Returns the current user's role in the selected organization.
 * Derives from useUserOrganizations() data — no extra query needed beyond the
 * (cached) super-admin check that backs the non-member fallback.
 *
 * Under an `/org/:orgId` route the hook is LOADING until the organization
 * context has caught up with the URL org (`OrgLayout` syncs it in an effect,
 * so the first render of a deep link has no — or the previous — selected
 * org). Without that wait the role reads as null, `can()` as false, and the
 * scope guards redirect a member who may enter.
 */
export function useCurrentUserOrgRole(): CurrentUserOrgRole {
  const { selectedOrganization } = useOrganization();
  const { orgId: routeOrgId } = useParams<{ orgId?: string }>();
  const { data: orgs, isLoading: orgsLoading } = useUserOrganizations();
  const { data: isSuperAdmin, isLoading: superAdminLoading } = useIsSuperAdmin();

  const selectedOrgId = selectedOrganization?.id;
  // The URL org, when there is one, is the org being asked about.
  const contextSyncing = !!routeOrgId && selectedOrgId !== routeOrgId;

  const role = useMemo(() => {
    if (orgsLoading || !orgs || !selectedOrgId || contextSyncing) return null;
    return orgs.find(o => o.id === selectedOrgId)?.user_role ?? null;
  }, [orgs, selectedOrgId, orgsLoading, contextSyncing]);

  // The super-admin answer only matters when there is no membership row.
  const isLoading = orgsLoading || contextSyncing || (role === null && superAdminLoading);
  const scopeRole = effectiveScopeRole(role, isSuperAdmin);

  const can = useCallback(
    (scope: AreaScope) => !isLoading && orgRoleHasScope(scopeRole, scope),
    [isLoading, scopeRole],
  );

  return useMemo(
    () => ({
      role,
      can,
      isAdmin: role === 'admin',
      isMember: role !== null,
      isLoading,
    }),
    [role, can, isLoading],
  );
}

/**
 * Scope check against ANY of the user's orgs (not just the selected one) —
 * for cross-org surfaces such as the inbox, whose rows link into the org the
 * record belongs to. Same rule as `useCurrentUserOrgRole().can`; false while
 * loading.
 */
export function useCanInOrg(): (orgId: string | null | undefined, scope: AreaScope) => boolean {
  const { data: orgs, isLoading: orgsLoading } = useUserOrganizations();
  const { data: isSuperAdmin, isLoading: superAdminLoading } = useIsSuperAdmin();
  return useCallback(
    (orgId, scope) => {
      if (!orgId || orgsLoading || superAdminLoading) return false;
      const role = orgs?.find(o => o.id === orgId)?.user_role ?? null;
      return orgRoleHasScope(effectiveScopeRole(role, isSuperAdmin), scope);
    },
    [orgs, orgsLoading, superAdminLoading, isSuperAdmin],
  );
}
